/**
 * Auth API
 * Contains all API functions related to authentication and user management
 */

import { getLocalData, saveLocalData } from './ai.api';
import { apiGet, apiPost } from './apiClient';

const reportUserURL = '/report/user';

export interface LoginParams {
    username: string;
    password: string;
}

export interface RegisterParams {
    code: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    username: string;
    securityQuestion: string;
    securityAnswer: string;
    firebaseUid?: string;
}

export interface ResetPasswordParams {
    username: string;
    password: string;
    code: string;
}

export interface UpdateUserParams {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    username?: string;
    bio?: string;
    avatar?: string;
    banner?: string;
    subscribed?: string[];
}

export interface UpdatePasswordParams {
    currentPassword: string;
    newPassword: string;
}

export interface DeactivateParams {
    reason: string;
    feedback?: string;
}

export interface ReportUserParams {
    id: string;
    idea: string;
    reportedBy: string;
    dateReported: string;
}

export interface SendRecoveryCodeParams {
    email: string;
    username?: string;
}

/**
 * Log in a user
 * @param username The username
 * @param password The password
 * @returns Promise with login result
 */
export const login = async (params: LoginParams) => {
    return apiGet(
        `/login?username=${encodeURIComponent(params.username)}&password=${encodeURIComponent(params.password)}`,
        {},
        '',
    );
};

/**
 * Log out the current user
 * @returns Promise with logout result
 */
export const logout = async () => {
    return apiPost('/logout', {}, {}, 'authenticated', true);
};

/**
 * Register a new user
 * @param params User registration data
 * @returns Promise with registration result
 */
export const register = async (params: RegisterParams, extras: any) => {
    const data = {
        code: params.code,
        firstName: params.firstName,
        lastName: params.lastName,
        email: params.email,
        password: params.password,
        username: params.username,
        securityQuestion: params.securityQuestion,
        securityAnswer: params.securityAnswer,
        firebaseUid: params.firebaseUid,
        ...extras,
    };
    return apiPost('/register', data, {}, '');
};

/**
 * Send a forgot password email using the backend
 * @param email The user's email
 * @param username The user's username
 * @returns Promise with result
 */
export const forgotPassword = async (email: string, username: string) => {
    return apiPost('/auth/forgot-password', { email, username }, {}, '');
};

/**
 * Send an email verification link using the backend
 * @param email The user's email
 * @returns Promise with result
 */
export const sendVerificationEmail = async (email: string) => {
    return apiPost('/auth/send-verification', { email }, {}, '');
};

/**
 * Reset a user's password
 * @param params Password reset parameters
 * @returns Promise with password reset result
 */
export const resetPassword = async (params: ResetPasswordParams) => {
    return apiPost(
        `/user/update/password`,
        params,
        {},
        '',
        true,
    );
};

/**
 * Validate an email address
 * @param email The email to validate
 * @returns Promise with validation result
 */
export const validateEmail = async (email: string) => {
    return apiGet(`/email?email=${encodeURIComponent(email)}`, {}, '');
};

/**
 * Get a user by username
 * @param username The username to look up
 * @returns Promise with user data
 */
export const getUser = async (username: string) => {
    return apiGet(`/user/${username}`, {}, '');
};

/**
 * Send a recovery code to the user's email
 */
export const apiSendRecoveryCode = async (params: SendRecoveryCodeParams) => {
    return apiPost('/user/send-code', params, {}, '');
};

/**
 * Report a user
 * @param params Report parameters
 * @returns Promise with report result
 */
export const reportUser = async (params: ReportUserParams) => {
    return apiGet(
        `${reportUserURL}?id=${encodeURIComponent(
            params.id,
        )}&idea=${encodeURIComponent(params.idea)}&reportedBy=${encodeURIComponent(
            params.reportedBy,
        )}&dateReported=${encodeURIComponent(params.dateReported)}`,
        {},
        '',
    );
};

/**
 * Get a user by ID
 * @param id The user ID
 * @returns Promise with user data
 */
export const getUserById = async (id: string) => {
    return apiGet(`/user/id/${id}`, {}, '');
};

/**
 * Update a user
 * @param params User data to update
 * @returns Promise with update result
 */
export const updateUser = async (params: UpdateUserParams) => {
    return apiPost('/user/update', params, {}, 'authenticated', true);
};

/**
 * Update a user's password
 * @param params Password update parameters
 * @returns Promise with update result
 */
export const updatePassword = async (params: UpdatePasswordParams) => {
    return apiPost('/user/update/password', params, {}, '');
};

/**
 * Deactivate a user account
 * @param params Deactivation parameters
 * @returns Promise with deactivation result
 */
export const deactivate = async (params: DeactivateParams) => {
    return apiPost('/user/deactivate', params, {}, '');
};

/**
 * Subscribe a user to another user
 * @param type 'add' or 'remove'
 * @param id The user ID
 * @returns Promise with subscription result
 */
export const subscribeUser = async (type: string, id: string, user: any) => {
    const storage = await getLocalData('idearify-app-storage');
    const subscribed = user.subscribed ? user.subscribed : [];
    if (type === 'add' && !(id in user.subscribed)) {
        subscribed.push(id);
    } else {
        const idx = user.subscribed.indexOf(id);
        subscribed.splice(idx, 1);
    }
    const newUser = { ...user, subscribed };
    const response = await updateUser(newUser);
    if (response.data) {
        saveLocalData('idearify-app-storage', { ...storage, state: { ...(storage?.state || {}), user: newUser } });
    } else {
        throw new Error('Failed to subscribe user');
    }
}

/**
 * Update the subscribers of a user
 * @param type 'add' or 'remove'
 * @param user The user to update
 * @returns void
 */
export const updateSubscribers = async (type: string, user: any) => {
    let subscribers = user.subscribers ? user.subscribers : 0;
    if (type === 'add') {
        subscribers += 1;
    } else if (type === 'remove') {
        subscribers -= 1;
    } else {
        return;
    }
    await updateUser({ ...user, subscribers });
}